import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import DataTable, { TableColumn } from "react-data-table-component";
import {
  BsArrowCounterclockwise,
  BsCheckCircle,
  BsCloudArrowUp,
  BsDownload,
  BsEye,
  BsFileEarmarkPdf,
  BsFunnel,
  BsImage,
  BsPaperclip,
  BsPencilSquare,
  BsPersonBadge,
  BsPlusCircle,
  BsSearch,
  BsTrash,
  BsXCircle,
  BsCameraVideo,
} from "react-icons/bs";
import {
  AccionEvidencia,
  DTOBitacoraEvidencia,
  DTOGestionarBitacoraEvidencia,
  DTOInventarioArchivoAdjunto,
  DTOOpcionEvidencia,
  EstatusEvidencia,
  CategoriaCatalogoEvidencia,
} from "../../../DTO/Inventario/DTOBitacoraEvidencia";
import { EmpleadoMasterDto } from "../CatSistemas/DTO/EmpleadoMasterDto";
import InventarioService from "../../../Services/Catalogos/InventarioService";
import ControlContrasenasService from "../../../Services/Catalogos/CatSistemas";
import { MsgInformativo } from "../../../Utils/Toast/msgInformativo";
import "./BitacoraEvidencias.css";

interface FormularioEvidencia {
  tipoEvidencia: string;
  titulo: string;
  fechaEvento: string;
  localidad: string;
  areaRelacionada: string;
  departamento: string;
  personasAfectadas: string;
  numEmpleadoResponsableInterno: string;
  responsableExterno: string;
  origen: string;
  fechaCierre: string;
  descripcion: string;
  accionRealizada: string;
  notas: string;
  estatus: EstatusEvidencia;
}

type ModoFormulario = "INSERTAR" | "ACTUALIZAR" | "COMPLETAR";

type CampoCatalogo =
  | "tipoEvidencia"
  | "titulo"
  | "localidad"
  | "areaRelacionada"
  | "departamento"
  | "personasAfectadas";

const configuracionCatalogos: Record<
  CategoriaCatalogoEvidencia,
  { campo: CampoCatalogo; etiqueta: string; maxLength: number }
> = {
  TIPO_EVIDENCIA: {
    campo: "tipoEvidencia",
    etiqueta: "tipo de evidencia",
    maxLength: 50,
  },
  TITULO: { campo: "titulo", etiqueta: "título de la evidencia", maxLength: 200 },
  LOCALIDAD: { campo: "localidad", etiqueta: "localidad", maxLength: 50 },
  AREA_RELACIONADA: {
    campo: "areaRelacionada",
    etiqueta: "área relacionada",
    maxLength: 100,
  },
  DEPARTAMENTO: {
    campo: "departamento",
    etiqueta: "departamento",
    maxLength: 100,
  },
  PERSONA_AFECTADA: {
    campo: "personasAfectadas",
    etiqueta: "persona o cliente relacionado",
    maxLength: 200,
  },
};

const fechaLocalParaInput = (valor?: string | null) => {
  const fecha = valor ? new Date(valor) : new Date();
  const local = new Date(fecha.getTime() - fecha.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const formularioVacio = (): FormularioEvidencia => ({
  tipoEvidencia: "",
  titulo: "",
  fechaEvento: fechaLocalParaInput(),
  localidad: "",
  areaRelacionada: "",
  departamento: "",
  personasAfectadas: "",
  numEmpleadoResponsableInterno: "",
  responsableExterno: "",
  origen: "",
  fechaCierre: "",
  descripcion: "",
  accionRealizada: "",
  notas: "",
  estatus: "PENDIENTE",
});

const obtenerMensajeError = (error: any) =>
  error?.response?.data?.mensaje ||
  error?.response?.data?.message ||
  error?.message ||
  "Ocurrió un error inesperado.";

const BitacoraEvidencias = () => {
  const [fallas, setFallas] = useState<DTOBitacoraEvidencia[]>([]);
  const [catalogos, setCatalogos] = useState<DTOOpcionEvidencia[]>([]);
  const [empleados, setEmpleados] = useState<EmpleadoMasterDto[]>([]);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [guardandoCatalogo, setGuardandoCatalogo] = useState(false);
  const [showMsg, setShowMsg] = useState(false);
  const [msg, setMsg] = useState("");
  const [header, setHeader] = useState("");
  const [msgColor, setMsgColor] = useState("primary");

  const mostrarMensaje = useCallback(
    (encabezado: string, mensaje: string, color: string) => {
      setHeader(encabezado);
      setMsg(mensaje);
      setMsgColor(color);
      setShowMsg(true);
    },
    []
  );

  const [texto, setTexto] = useState("");
  const [tipoEvidencia, setTipoEvidencia] = useState("");
  const [estatus, setEstatus] = useState("");
  const [localidad, setLocalidad] = useState("");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");

  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [modo, setModo] = useState<ModoFormulario>("INSERTAR");
  const [seleccionada, setSeleccionada] =
    useState<DTOBitacoraEvidencia | null>(null);
  const [formulario, setFormulario] = useState<FormularioEvidencia>(formularioVacio());
  const [textoResponsableInterno, setTextoResponsableInterno] = useState("");
  const [detalle, setDetalle] = useState<DTOBitacoraEvidencia | null>(null);
  const [archivosAdjuntos, setArchivosAdjuntos] = useState<
    DTOInventarioArchivoAdjunto[]
  >([]);
  const [archivosSeleccionados, setArchivosSeleccionados] = useState<File[]>([]);
  const [descripcionArchivo, setDescripcionArchivo] = useState("");
  const [cargandoArchivos, setCargandoArchivos] = useState(false);
  const [eliminandoArchivoId, setEliminandoArchivoId] = useState<number | null>(
    null
  );
  const [mostrarNuevoCatalogo, setMostrarNuevoCatalogo] = useState(false);
  const [tipoNuevoCatalogo, setTipoNuevoCatalogo] =
    useState<CategoriaCatalogoEvidencia>("TIPO_EVIDENCIA");
  const [valorNuevoCatalogo, setValorNuevoCatalogo] = useState("");

  const cargarCatalogos = useCallback(async () => {
    try {
      const respuesta =
        await InventarioService.obtenerCatalogosEvidencias();
      setCatalogos(respuesta.data);
      return respuesta.data;
    } catch (e) {
      mostrarMensaje("Error", obtenerMensajeError(e), "danger");
      return [] as DTOOpcionEvidencia[];
    }
  }, [mostrarMensaje]);

  const cargarEmpleados = useCallback(async () => {
    try {
      const respuesta = await ControlContrasenasService.GetEmpleadosMaster();
      setEmpleados((respuesta.data ?? []).filter((empleado) => empleado.activo));
    } catch (e) {
      mostrarMensaje("Error", obtenerMensajeError(e), "danger");
    }
  }, [mostrarMensaje]);

  const cargarEvidencias = useCallback(async () => {
    setCargando(true);
    try {
      const respuesta = await InventarioService.obtenerBitacoraEvidencias({
        texto: texto.trim() || null,
        tipoEvidencia: tipoEvidencia || null,
        estatus:
          estatus === "INACTIVA"
            ? null
            : (estatus || null) as EstatusEvidencia | null,
        localidad: localidad.trim() || null,
        fechaInicio: fechaInicio || null,
        fechaFin: fechaFin || null,
        soloActivos: estatus !== "INACTIVA",
      });
      setFallas(
        estatus === "INACTIVA"
          ? respuesta.data.filter((falla) => !falla.activo)
          : respuesta.data
      );
    } catch (e) {
      mostrarMensaje("Error", obtenerMensajeError(e), "danger");
    } finally {
      setCargando(false);
    }
  }, [estatus, fechaFin, fechaInicio, localidad, mostrarMensaje, texto, tipoEvidencia]);

  useEffect(() => {
    cargarEvidencias();
  }, [cargarEvidencias]);

  useEffect(() => {
    cargarCatalogos();
  }, [cargarCatalogos]);

  useEffect(() => {
    cargarEmpleados();
  }, [cargarEmpleados]);

  const resumen = useMemo(
    () => ({
      total: fallas.length,
      abiertas: fallas.filter((x) => x.activo && x.estatus === "PENDIENTE").length,
      resueltas: fallas.filter((x) => x.activo && x.estatus === "COMPLETADA").length,
      eliminadas: fallas.filter((x) => !x.activo).length,
    }),
    [fallas]
  );

  const formDesdeEvidencia = (falla: DTOBitacoraEvidencia): FormularioEvidencia => ({
    tipoEvidencia: falla.tipoEvidencia || "",
    titulo: falla.titulo || "",
    fechaEvento: fechaLocalParaInput(falla.fechaEvento),
    localidad: falla.localidad || "",
    areaRelacionada: falla.areaRelacionada || "",
    departamento: falla.departamento || "",
    personasAfectadas: falla.personasAfectadas || "",
    numEmpleadoResponsableInterno:
      falla.numEmpleadoResponsableInterno || "",
    responsableExterno: falla.responsableExterno || "",
    origen: falla.origen || "",
    fechaCierre: falla.fechaCierre
      ? fechaLocalParaInput(falla.fechaCierre)
      : "",
    descripcion: falla.descripcion || "",
    accionRealizada: falla.accionRealizada || "",
    notas: falla.notas || "",
    estatus: falla.estatus,
  });

  const abrirNueva = () => {
    setSeleccionada(null);
    setModo("INSERTAR");
    setFormulario(formularioVacio());
    setTextoResponsableInterno("");
    setArchivosAdjuntos([]);
    setArchivosSeleccionados([]);
    setDescripcionArchivo("");
    setMostrarFormulario(true);
  };

  const cargarArchivos = useCallback(
    async (idEvidencia: number) => {
      setCargandoArchivos(true);
      try {
        const respuesta =
          await InventarioService.obtenerArchivosEvidencia(idEvidencia, true);
        setArchivosAdjuntos(respuesta.data ?? []);
      } catch (e) {
        setArchivosAdjuntos([]);
        mostrarMensaje("Error", obtenerMensajeError(e), "danger");
      } finally {
        setCargandoArchivos(false);
      }
    },
    [mostrarMensaje]
  );

  const textoEmpleado = (falla: DTOBitacoraEvidencia) => {
    if (!falla.numEmpleadoResponsableInterno) return "";
    const empleado = empleados.find(
      (item) => item.numEmpleado === falla.numEmpleadoResponsableInterno
    );
    const nombre = empleado?.name || falla.nombreResponsableInterno;
    return nombre
      ? `${falla.numEmpleadoResponsableInterno} - ${nombre}`
      : falla.numEmpleadoResponsableInterno;
  };

  const abrirEditar = (falla: DTOBitacoraEvidencia) => {
    setSeleccionada(falla);
    setModo("ACTUALIZAR");
    setFormulario(formDesdeEvidencia(falla));
    setTextoResponsableInterno(textoEmpleado(falla));
    setArchivosSeleccionados([]);
    setDescripcionArchivo("");
    setArchivosAdjuntos([]);
    cargarArchivos(falla.idEvidencia);
    setMostrarFormulario(true);
  };

  const abrirCompletar = (falla: DTOBitacoraEvidencia) => {
    setSeleccionada(falla);
    setModo("COMPLETAR");
    setFormulario({
      ...formDesdeEvidencia(falla),
      fechaCierre: falla.fechaCierre
        ? fechaLocalParaInput(falla.fechaCierre)
        : fechaLocalParaInput(),
      estatus: "COMPLETADA",
    });
    setTextoResponsableInterno(textoEmpleado(falla));
    setArchivosSeleccionados([]);
    setDescripcionArchivo("");
    setArchivosAdjuntos([]);
    cargarArchivos(falla.idEvidencia);
    setMostrarFormulario(true);
  };

  const abrirDetalle = (falla: DTOBitacoraEvidencia) => {
    setDetalle(falla);
    setArchivosAdjuntos([]);
    cargarArchivos(falla.idEvidencia);
  };

  const formatearTamanio = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const seleccionarArchivos = (evento: ChangeEvent<HTMLInputElement>) => {
    const nuevos = Array.from(evento.target.files ?? []);
    evento.target.value = "";
    if (!nuevos.length) return;

    const extensionesPermitidas = ["png", "jpg", "jpeg", "mp4", "pdf"];
    const activos = archivosAdjuntos.filter((archivo) => archivo.archivoDisponible);
    if (activos.length + archivosSeleccionados.length + nuevos.length > 10) {
      mostrarMensaje(
        "Informativo",
        "Cada evidencia permite como máximo 10 archivos activos.",
        "warning"
      );
      return;
    }

    for (const archivo of nuevos) {
      const extension = archivo.name.split(".").pop()?.toLowerCase() || "";
      const limite = extension === "mp4" ? 100 * 1024 * 1024 : 20 * 1024 * 1024;
      if (!extensionesPermitidas.includes(extension)) {
        mostrarMensaje(
          "Informativo",
          `${archivo.name}: solamente se permiten PNG, JPG, JPEG, MP4 y PDF.`,
          "warning"
        );
        return;
      }
      if (archivo.size > limite) {
        mostrarMensaje(
          "Informativo",
          `${archivo.name} supera el límite de ${extension === "mp4" ? "100" : "20"} MB.`,
          "warning"
        );
        return;
      }
    }

    const totalBytes = [...activos, ...archivosSeleccionados, ...nuevos].reduce(
      (total, archivo) =>
        total + ("tamanioBytes" in archivo ? archivo.tamanioBytes : archivo.size),
      0
    );
    if (totalBytes > 200 * 1024 * 1024) {
      mostrarMensaje(
        "Informativo",
        "El total de archivos activos de la evidencia no puede superar 200 MB.",
        "warning"
      );
      return;
    }

    setArchivosSeleccionados((actuales) => [...actuales, ...nuevos]);
  };

  const quitarArchivoSeleccionado = (indice: number) => {
    setArchivosSeleccionados((actuales) =>
      actuales.filter((_, posicion) => posicion !== indice)
    );
  };

  const obtenerArchivo = async (
    archivo: DTOInventarioArchivoAdjunto,
    descargar: boolean
  ) => {
    try {
      const respuesta = await InventarioService.obtenerContenidoArchivo(
        archivo.idArchivoAdjunto,
        !descargar
      );
      const url = URL.createObjectURL(respuesta.data);
      if (descargar) {
        const enlace = document.createElement("a");
        enlace.href = url;
        enlace.download = archivo.nombreOriginal;
        document.body.appendChild(enlace);
        enlace.click();
        enlace.remove();
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      mostrarMensaje("Error", obtenerMensajeError(e), "danger");
    }
  };

  const eliminarArchivo = async (archivo: DTOInventarioArchivoAdjunto) => {
    if (
      !window.confirm(
        `¿Eliminar físicamente ${archivo.nombreOriginal}? El historial permanecerá visible.`
      )
    ) {
      return;
    }

    setEliminandoArchivoId(archivo.idArchivoAdjunto);
    try {
      const respuesta = await InventarioService.eliminarArchivoAdjunto(
        archivo.idArchivoAdjunto
      );
      mostrarMensaje("Éxito", respuesta.data.mensaje, "success");
      await cargarArchivos(archivo.idRegistro);
    } catch (e) {
      mostrarMensaje("Error", obtenerMensajeError(e), "danger");
    } finally {
      setEliminandoArchivoId(null);
    }
  };

  const cambiarCampo = (campo: keyof FormularioEvidencia, valor: string) => {
    setFormulario((actual) => ({ ...actual, [campo]: valor }));
  };

  const cambiarResponsableInterno = (
    evento: ChangeEvent<HTMLInputElement>
  ) => {
    const texto = evento.target.value;
    setTextoResponsableInterno(texto);

    const empleado = empleados.find(
      (item) => `${item.numEmpleado} - ${item.name}` === texto
    );

    cambiarCampo(
      "numEmpleadoResponsableInterno",
      empleado?.numEmpleado || ""
    );
  };

  const opcionesCatalogo = (
    categoria: CategoriaCatalogoEvidencia,
    valorActual = ""
  ) => {
    const opciones = catalogos.filter(
      (opcion) => opcion.categoria === categoria && opcion.activo
    );

    if (
      valorActual &&
      !opciones.some(
        (opcion) =>
          opcion.valor.toLocaleLowerCase() === valorActual.toLocaleLowerCase()
      )
    ) {
      return [
        ...opciones,
        {
          idOpcionEvidencia: -1,
          categoria,
          valor: valorActual,
          orden: Number.MAX_SAFE_INTEGER,
          activo: true,
        },
      ];
    }

    return opciones;
  };

  const abrirNuevoCatalogo = (categoria: CategoriaCatalogoEvidencia) => {
    setTipoNuevoCatalogo(categoria);
    setValorNuevoCatalogo("");
    setMostrarNuevoCatalogo(true);
  };

  const guardarNuevoCatalogo = async (evento: FormEvent) => {
    evento.preventDefault();
    const valor = valorNuevoCatalogo.trim();

    if (!valor) {
      mostrarMensaje(
        "Informativo",
        "Captura el nuevo valor del catálogo.",
        "warning"
      );
      return;
    }

    setGuardandoCatalogo(true);
    try {
      const respuesta = await InventarioService.gestionarCatalogoEvidencia({
        accion: "INSERTAR",
        categoria: tipoNuevoCatalogo,
        valor,
      });
      const opcionesActualizadas = await cargarCatalogos();
      const valorGuardado =
        opcionesActualizadas.find(
          (opcion) =>
            opcion.categoria === tipoNuevoCatalogo &&
            opcion.valor.toLocaleLowerCase() === valor.toLocaleLowerCase()
        )?.valor || valor;
      cambiarCampo(
        configuracionCatalogos[tipoNuevoCatalogo].campo,
        valorGuardado
      );
      setMostrarNuevoCatalogo(false);
      mostrarMensaje("Éxito", respuesta.data.mensaje, "success");
    } catch (e) {
      mostrarMensaje("Error", obtenerMensajeError(e), "danger");
    } finally {
      setGuardandoCatalogo(false);
    }
  };

  const selectorCatalogo = (
    categoria: CategoriaCatalogoEvidencia,
    columnas: number,
    requerido = false
  ) => {
    const configuracion = configuracionCatalogos[categoria];
    const valor = formulario[configuracion.campo];

    return (
      <Col md={columnas}>
        <Form.Label className="text-capitalize">
          {configuracion.etiqueta}{requerido ? " *" : ""}
        </Form.Label>
        <InputGroup className="selector-catalogo-falla">
          <Form.Select
            required={requerido}
            value={valor}
            onChange={(e) =>
              cambiarCampo(configuracion.campo, e.target.value)
            }
            disabled={modo === "COMPLETAR"}
          >
            <option value="">
              {requerido ? "Selecciona una opción" : "Sin especificar"}
            </option>
            {opcionesCatalogo(categoria, valor).map((opcion) => (
              <option
                key={`${categoria}-${opcion.idOpcionEvidencia}-${opcion.valor}`}
                value={opcion.valor}
              >
                {opcion.valor}
              </option>
            ))}
          </Form.Select>
          <Button
            type="button"
            variant="outline-primary"
            title={`Agregar ${configuracion.etiqueta}`}
            aria-label={`Agregar ${configuracion.etiqueta}`}
            onClick={() => abrirNuevoCatalogo(categoria)}
            disabled={modo === "COMPLETAR"}
          >
            <BsPlusCircle />
          </Button>
        </InputGroup>
      </Col>
    );
  };

  const guardar = async (evento: FormEvent) => {
    evento.preventDefault();
    if (!formulario.tipoEvidencia || !formulario.titulo.trim() || !formulario.fechaEvento) {
      mostrarMensaje(
        "Informativo",
        "El tipo, el título y la fecha del evento son obligatorios.",
        "warning"
      );
      return;
    }
    if (modo === "COMPLETAR" && !formulario.fechaCierre) {
      mostrarMensaje(
        "Informativo",
        "Captura la fecha y hora de cierre.",
        "warning"
      );
      return;
    }
    if (
      textoResponsableInterno.trim() &&
      !formulario.numEmpleadoResponsableInterno
    ) {
      mostrarMensaje(
        "Informativo",
        "Selecciona un responsable interno válido de la lista.",
        "warning"
      );
      return;
    }

    const dto: DTOGestionarBitacoraEvidencia = {
      accion: modo,
      idEvidencia: seleccionada?.idEvidencia || null,
      tipoEvidencia: formulario.tipoEvidencia,
      titulo: formulario.titulo.trim(),
      fechaEvento: formulario.fechaEvento,
      localidad: formulario.localidad.trim() || null,
      areaRelacionada: formulario.areaRelacionada.trim() || null,
      departamento: formulario.departamento.trim() || null,
      personasAfectadas: formulario.personasAfectadas.trim() || null,
      numEmpleadoResponsableInterno:
        formulario.numEmpleadoResponsableInterno.trim() || null,
      responsableExterno: formulario.responsableExterno.trim() || null,
      origen: formulario.origen.trim() || null,
      fechaCierre: formulario.fechaCierre || null,
      descripcion: formulario.descripcion.trim() || null,
      accionRealizada: formulario.accionRealizada.trim() || null,
      notas: formulario.notas.trim() || null,
      estatus: modo === "COMPLETAR" ? "COMPLETADA" : formulario.estatus,
    };

    setGuardando(true);
    try {
      const respuesta = await InventarioService.gestionarBitacoraEvidencia(dto);
      const idEvidencia =
        respuesta.data.idEvidencia ?? seleccionada?.idEvidencia ?? null;
      let mensaje = respuesta.data.mensaje;

      if (archivosSeleccionados.length > 0) {
        if (!idEvidencia) {
          throw new Error(
            "La evidencia se guardó, pero el servidor no devolvió su identificador para cargar los archivos."
          );
        }

        try {
          const carga = await InventarioService.subirArchivosEvidencia(
            idEvidencia,
            archivosSeleccionados,
            modo === "COMPLETAR" ? "EVIDENCIA_CIERRE" : "EVIDENCIA",
            descripcionArchivo
          );
          mensaje = `${mensaje} ${carga.data.mensaje}`;
        } catch (errorArchivo) {
          setMostrarFormulario(false);
          setArchivosSeleccionados([]);
          await cargarEvidencias();
          mostrarMensaje(
            "Atención",
            `La evidencia se guardó correctamente, pero no fue posible cargar los archivos: ${obtenerMensajeError(errorArchivo)}`,
            "warning"
          );
          return;
        }
      }

      mostrarMensaje("Éxito", mensaje, "success");
      setMostrarFormulario(false);
      setArchivosSeleccionados([]);
      await cargarEvidencias();
    } catch (e) {
      mostrarMensaje("Error", obtenerMensajeError(e), "danger");
    } finally {
      setGuardando(false);
    }
  };

  const ejecutarAccion = async (
    accion: Extract<AccionEvidencia, "ELIMINAR" | "REACTIVAR">,
    falla: DTOBitacoraEvidencia
  ) => {
    if (
      accion === "ELIMINAR" &&
      !window.confirm(`¿Deseas eliminar la evidencia #${falla.idEvidencia}?`)
    ) {
      return;
    }

    try {
      const respuesta = await InventarioService.gestionarBitacoraEvidencia({
        accion,
        idEvidencia: falla.idEvidencia,
      });
      mostrarMensaje("Éxito", respuesta.data.mensaje, "success");
      await cargarEvidencias();
    } catch (e) {
      mostrarMensaje("Error", obtenerMensajeError(e), "danger");
    }
  };

  const limpiarFiltros = () => {
    setTexto("");
    setTipoEvidencia("");
    setEstatus("");
    setLocalidad("");
    setFechaInicio("");
    setFechaFin("");
  };

  const formatoFecha = (valor?: string | null) =>
    valor
      ? new Intl.DateTimeFormat("es-MX", {
          dateStyle: "short",
          timeStyle: "short",
        }).format(new Date(valor))
      : "—";

  const badgeEstatus = (valor: EstatusEvidencia) => {
    const fondo =
      valor === "PENDIENTE" ? "warning" : valor === "COMPLETADA" ? "success" : "secondary";
    return (
      <Badge bg={fondo} text={valor === "PENDIENTE" ? "dark" : undefined}>
        {valor}
      </Badge>
    );
  };

  const iconoArchivo = (archivo: { tipoContenido?: string; extension: string }) => {
    if (archivo.tipoContenido?.startsWith("image/") || [".png", ".jpg", ".jpeg"].includes(archivo.extension.toLowerCase())) {
      return <BsImage />;
    }
    if (archivo.tipoContenido === "application/pdf" || archivo.extension.toLowerCase() === ".pdf") {
      return <BsFileEarmarkPdf />;
    }
    return <BsCameraVideo />;
  };

  const seccionArchivos = (
    permitirCarga: boolean,
    permitirEliminar: boolean
  ) => (
    <div className="archivos-evidencia">
      <div className="archivos-encabezado">
        <div className="archivos-titulo-wrap">
          <span className="archivos-titulo-icono"><BsPaperclip /></span>
          <div>
            <div className="archivos-titulo">Archivos de evidencia</div>
            <div className="archivos-subtitulo">
              Fotografías, documentos o videos que comprueban la actividad.
            </div>
          </div>
        </div>
        <div className="archivo-limites">
          <span>PNG · JPG · PDF <strong>20 MB</strong></span>
          <span>MP4 <strong>100 MB</strong></span>
          <span>Máximo <strong>10 archivos</strong></span>
        </div>
      </div>

      {permitirCarga && (
        <div className="archivo-selector mb-3">
          <input
            id="selector-archivos-evidencia"
            className="archivo-input-oculto"
            type="file"
            multiple
            accept=".png,.jpg,.jpeg,.mp4,.pdf,image/png,image/jpeg,video/mp4,application/pdf"
            onChange={seleccionarArchivos}
            disabled={guardando}
          />
          <label
            htmlFor="selector-archivos-evidencia"
            className={`archivo-dropzone ${guardando ? "archivo-dropzone-deshabilitado" : ""}`}
          >
            <span className="archivo-dropzone-icono"><BsCloudArrowUp /></span>
            <span className="archivo-dropzone-texto">
              <strong>Seleccionar archivos</strong>
              <small>Haz clic aquí para buscar uno o varios archivos.</small>
            </span>
            <span className="archivo-dropzone-boton">Examinar</span>
          </label>
          <div className="archivo-descripcion">
            <Form.Label>Descripción de los archivos <span>(opcional)</span></Form.Label>
            <Form.Control
              maxLength={250}
              placeholder="Ej. fotografías del mantenimiento realizado"
              value={descripcionArchivo}
              onChange={(e) => setDescripcionArchivo(e.target.value)}
              disabled={guardando}
            />
            <small>Capacidad total disponible por evidencia: 200 MB.</small>
          </div>
        </div>
      )}

      {archivosSeleccionados.length > 0 && (
        <div className="archivos-grupo archivos-pendientes mb-3">
          <div className="archivos-grupo-titulo">
            <span>Listos para cargar</span>
            <Badge bg="primary" pill>{archivosSeleccionados.length}</Badge>
            <small>Se guardarán junto con la evidencia</small>
          </div>
          <div className="archivos-grid">
            {archivosSeleccionados.map((archivo, indice) => (
              <div className="archivo-item archivo-pendiente" key={`${archivo.name}-${archivo.lastModified}-${indice}`}>
                <div className="archivo-icono">
                  {iconoArchivo({ extension: `.${archivo.name.split(".").pop() || ""}` })}
                </div>
                <div className="archivo-informacion">
                  <strong title={archivo.name}>{archivo.name}</strong>
                  <small>{formatearTamanio(archivo.size)}</small>
                  <span className="archivo-estado archivo-estado-pendiente">Pendiente de carga</span>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline-danger"
                  title="Quitar de la selección"
                  onClick={() => quitarArchivoSeleccionado(indice)}
                  disabled={guardando}
                >
                  <BsXCircle />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {cargandoArchivos ? (
        <div className="text-center text-muted py-3">
          <Spinner size="sm" className="me-2" />Cargando archivos...
        </div>
      ) : archivosAdjuntos.length === 0 ? (
        <div className="archivo-vacio">
          <span><BsPaperclip /></span>
          <strong>Sin archivos guardados</strong>
          <small>
            {permitirCarga
              ? "Puedes adjuntar comprobantes antes de guardar la evidencia."
              : "No se registraron comprobantes para esta evidencia."}
          </small>
        </div>
      ) : (
        <div className="archivos-grupo">
          <div className="archivos-grupo-titulo">
            <span>Archivos guardados</span>
            <Badge bg="secondary" pill>{archivosAdjuntos.length}</Badge>
          </div>
          <div className="archivos-grid">
            {archivosAdjuntos.map((archivo) => (
            <div
              className={`archivo-item ${!archivo.archivoDisponible ? "archivo-eliminado" : ""}`}
              key={archivo.idArchivoAdjunto}
            >
              <div className="archivo-icono">{iconoArchivo(archivo)}</div>
              <div className="archivo-informacion">
                <strong title={archivo.nombreOriginal}>{archivo.nombreOriginal}</strong>
                <small>
                  {formatearTamanio(archivo.tamanioBytes)} · {formatoFecha(archivo.fechaAlta)}
                </small>
                {archivo.descripcion && <small>{archivo.descripcion}</small>}
                {archivo.archivoDisponible && (
                  <span className="archivo-estado archivo-estado-disponible">Disponible</span>
                )}
                {!archivo.archivoDisponible && (
                  <>
                    <span className="archivo-estado archivo-estado-eliminado">Eliminado físicamente</span>
                    <small className="archivo-historial-eliminacion">
                      {archivo.fechaEliminacion ? formatoFecha(archivo.fechaEliminacion) : ""}
                      {archivo.nombreEmpleadoEliminacion || archivo.numEmpleadoEliminacion
                        ? ` · ${archivo.nombreEmpleadoEliminacion || archivo.numEmpleadoEliminacion}`
                        : ""}
                    </small>
                  </>
                )}
              </div>
              {archivo.archivoDisponible && (
                <div className="archivo-acciones">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-primary"
                    title="Abrir archivo"
                    onClick={() => obtenerArchivo(archivo, false)}
                  >
                    <BsEye /><span>Abrir</span>
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-secondary"
                    title="Descargar archivo"
                    onClick={() => obtenerArchivo(archivo, true)}
                  >
                    <BsDownload /><span>Descargar</span>
                  </Button>
                  {permitirEliminar && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline-danger"
                      title="Eliminar archivo físico"
                      onClick={() => eliminarArchivo(archivo)}
                      disabled={eliminandoArchivoId === archivo.idArchivoAdjunto}
                    >
                      {eliminandoArchivoId === archivo.idArchivoAdjunto ? (
                        <Spinner size="sm" />
                      ) : (
                        <><BsTrash /><span>Eliminar</span></>
                      )}
                    </Button>
                  )}
                </div>
              )}
            </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const botonesAccion = (
    falla: DTOBitacoraEvidencia,
    mostrarTexto = false
  ) => (
    <div className={`acciones-falla ${mostrarTexto ? "acciones-falla-movil" : ""}`}>
      <Button size="sm" variant="outline-primary" title="Ver detalle" onClick={() => abrirDetalle(falla)}>
        <BsEye /> {mostrarTexto && <span>Detalle</span>}
      </Button>
      {falla.activo && (
        <Button size="sm" variant="outline-secondary" title="Editar" onClick={() => abrirEditar(falla)}>
          <BsPencilSquare /> {mostrarTexto && <span>Editar</span>}
        </Button>
      )}
      {falla.activo && falla.estatus === "PENDIENTE" && (
        <Button size="sm" variant="outline-success" title="Completar" onClick={() => abrirCompletar(falla)}>
          <BsCheckCircle /> {mostrarTexto && <span>Completar</span>}
        </Button>
      )}
      {falla.activo ? (
        <Button size="sm" variant="outline-danger" title="Eliminar" onClick={() => ejecutarAccion("ELIMINAR", falla)}>
          <BsTrash /> {mostrarTexto && <span>Eliminar</span>}
        </Button>
      ) : (
        <Button size="sm" variant="outline-success" title="Reactivar" onClick={() => ejecutarAccion("REACTIVAR", falla)}>
          <BsArrowCounterclockwise /> {mostrarTexto && <span>Reactivar</span>}
        </Button>
      )}
    </div>
  );

  const columnas: TableColumn<DTOBitacoraEvidencia>[] = [
    { name: "ID", selector: (x) => x.idEvidencia, sortable: true, width: "70px" },
    {
      name: "Tipo",
      selector: (x) => x.tipoEvidencia,
      sortable: true,
      width: "145px",
    },
    {
      name: "Título / acción",
      selector: (x) => x.titulo,
      sortable: true,
      grow: 2,
      wrap: true,
    },
    {
      name: "Fecha del evento",
      selector: (x) => x.fechaEvento,
      format: (x) => formatoFecha(x.fechaEvento),
      sortable: true,
      width: "165px",
    },
    { name: "Localidad", selector: (x) => x.localidad || "—", sortable: true, wrap: true },
    { name: "Área", selector: (x) => x.areaRelacionada || "—", sortable: true, wrap: true },
    {
      name: "Responsables",
      cell: (x) => (
        <div className="py-2">
          <div>
            <small className="text-muted">Interno: </small>
            {x.nombreResponsableInterno ||
              x.numEmpleadoResponsableInterno ||
              "—"}
          </div>
          <div>
            <small className="text-muted">Externo: </small>
            {x.responsableExterno || "—"}
          </div>
        </div>
      ),
      grow: 1.5,
      wrap: true,
    },
    {
      name: "Estatus",
      cell: (x) => (
        <div className="d-flex flex-column align-items-start gap-1">
          {badgeEstatus(x.estatus)}
          {!x.activo && <Badge bg="danger">ELIMINADA / INACTIVA</Badge>}
        </div>
      ),
      sortable: true,
      selector: (x) => x.estatus,
      width: "165px",
    },
    {
      name: "Duración",
      selector: (x) => x.duracionTexto || "—",
      width: "120px",
    },
    {
      name: "Acciones",
      width: "210px",
      center: true,
      cell: (x) => botonesAccion(x),
    },
  ];

  return (
    <div className="container-fluid py-3 bitacora-fallas">
      <div className="d-flex justify-content-end mb-3">
        <Button className="boton-registrar" onClick={abrirNueva}>
          <BsPlusCircle className="me-2" />Registrar evidencia
        </Button>
      </div>

      <Row className="g-2 g-md-3 mb-3 resumen-fallas">
        {[
          ["Total mostrado", resumen.total, "primary"],
          ["Pendientes", resumen.abiertas, "warning"],
          ["Completadas", resumen.resueltas, "success"],
          ["Eliminadas / Inactivas", resumen.eliminadas, "secondary"],
        ].map(([titulo, valor, color]) => (
          <Col lg={3} xs={6} key={String(titulo)}>
            <Card className={`h-100 border-0 text-center resumen-card resumen-${color}`}>
              <Card.Body><div className="resumen-titulo">{titulo}</div><div className={`resumen-valor text-${color}`}>{valor}</div></Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="shadow-sm border-0 mb-3 filtros-card">
        <Card.Body>
          <div className="fw-semibold mb-2"><BsFunnel className="me-2" />Filtros del reporte</div>
          <Form onSubmit={(e) => { e.preventDefault(); cargarEvidencias(); }}>
            <div className="filtros-grid">
              <div className="filtro-campo filtro-buscar">
                <Form.Label>Buscar</Form.Label>
                <Form.Control value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Título, área, responsable, descripción..." />
              </div>
              <div className="filtro-campo">
                <Form.Label>Tipo de evidencia</Form.Label>
                <Form.Select value={tipoEvidencia} onChange={(e) => setTipoEvidencia(e.target.value)}>
                  <option value="">Todos los tipos</option>
                  {opcionesCatalogo("TIPO_EVIDENCIA", tipoEvidencia).map((opcion) => (
                    <option key={`filtro-tipo-${opcion.idOpcionEvidencia}`} value={opcion.valor}>
                      {opcion.valor}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="filtro-campo">
                <Form.Label>Estatus</Form.Label>
                <Form.Select value={estatus} onChange={(e) => setEstatus(e.target.value)}>
                  <option value="">Todos los estatus</option>
                  <option value="PENDIENTE">Pendientes</option>
                  <option value="COMPLETADA">Completadas</option>
                  <option value="INACTIVA">Eliminadas / Inactivas</option>
                </Form.Select>
              </div>
              <div className="filtro-campo">
                <Form.Label>Localidad</Form.Label>
                <Form.Select value={localidad} onChange={(e) => setLocalidad(e.target.value)}>
                  <option value="">Todas las localidades</option>
                  {opcionesCatalogo("LOCALIDAD", localidad).map((opcion) => (
                    <option
                      key={`filtro-localidad-${opcion.idOpcionEvidencia}-${opcion.valor}`}
                      value={opcion.valor}
                    >
                      {opcion.valor}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="filtro-campo">
                <Form.Label>Desde</Form.Label><Form.Control type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} />
              </div>
              <div className="filtro-campo">
                <Form.Label>Hasta</Form.Label><Form.Control type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} />
              </div>
              <div className="acciones-filtros">
                <Button type="submit"><BsSearch className="me-2" />Consultar</Button>
                <Button type="button" variant="outline-secondary" onClick={limpiarFiltros}>Limpiar</Button>
              </div>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <Card className="shadow-sm border-0 tabla-fallas-escritorio">
        <DataTable
          columns={columnas}
          data={fallas}
          progressPending={cargando}
          progressComponent={<div className="p-5"><Spinner animation="border" /></div>}
          noDataComponent="No hay evidencias que coincidan con los filtros."
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
          highlightOnHover
          striped
          responsive
          persistTableHead
          defaultSortFieldId={3}
          defaultSortAsc={false}
        />
      </Card>

      <div className="lista-fallas-movil">
        {cargando ? (
          <div className="text-center py-5"><Spinner animation="border" /></div>
        ) : fallas.length === 0 ? (
          <Card className="border-0 shadow-sm"><Card.Body className="text-center text-muted py-4">No hay evidencias que coincidan con los filtros.</Card.Body></Card>
        ) : fallas.map((falla) => (
          <Card className="border-0 shadow-sm falla-movil-card" key={falla.idEvidencia}>
            <Card.Body>
              <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                <div><small className="text-muted">{falla.tipoEvidencia} · Evidencia #{falla.idEvidencia}</small><div className="fw-bold falla-movil-titulo">{falla.titulo}</div></div>
                <div className="d-flex flex-column align-items-end gap-1">{badgeEstatus(falla.estatus)}{!falla.activo && <Badge bg="danger">ELIMINADA</Badge>}</div>
              </div>
              <div className="falla-movil-datos">
                <div><span>Evento</span><strong>{formatoFecha(falla.fechaEvento)}</strong></div>
                <div><span>Localidad</span><strong>{falla.localidad || "—"}</strong></div>
                <div><span>Área</span><strong>{falla.areaRelacionada || "—"}</strong></div>
                <div><span>Duración</span><strong>{falla.duracionTexto || "—"}</strong></div>
                <div><span>Responsable interno</span><strong>{falla.nombreResponsableInterno || falla.numEmpleadoResponsableInterno || "—"}</strong></div>
                <div><span>Responsable externo</span><strong>{falla.responsableExterno || "—"}</strong></div>
              </div>
              {botonesAccion(falla, true)}
            </Card.Body>
          </Card>
        ))}
      </div>

      <Modal className="bitacora-fallas" show={mostrarFormulario} onHide={() => !guardando && setMostrarFormulario(false)} size="xl" centered fullscreen="sm-down">
        <Form onSubmit={guardar}>
          <Modal.Header closeButton>
            <Modal.Title>{modo === "INSERTAR" ? "Registrar evidencia" : modo === "COMPLETAR" ? "Completar evidencia" : "Editar evidencia"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="g-3">
              {selectorCatalogo("TIPO_EVIDENCIA", 4, true)}
              {selectorCatalogo("TITULO", 4, true)}
              <Col md={4}><Form.Label>Fecha y hora del evento *</Form.Label><Form.Control required type="datetime-local" value={formulario.fechaEvento} onChange={(e) => cambiarCampo("fechaEvento", e.target.value)} disabled={modo === "COMPLETAR"} /></Col>
              {selectorCatalogo("LOCALIDAD", 4)}
              {selectorCatalogo("AREA_RELACIONADA", 4)}
              {selectorCatalogo("DEPARTAMENTO", 4)}
              {selectorCatalogo("PERSONA_AFECTADA", 6)}
              <Col md={6}><Form.Label>Fecha y hora de cierre {modo === "COMPLETAR" && "*"}</Form.Label><Form.Control required={modo === "COMPLETAR"} type="datetime-local" value={formulario.fechaCierre} onChange={(e) => cambiarCampo("fechaCierre", e.target.value)} /></Col>
              <Col md={6}>
                <Form.Label>Responsable interno</Form.Label>
                <InputGroup>
                  <InputGroup.Text><BsPersonBadge /></InputGroup.Text>
                  <Form.Control
                    type="text"
                    list="datalistResponsablesInternosFallas"
                    placeholder="Escribe nombre o número de empleado..."
                    value={textoResponsableInterno}
                    onChange={cambiarResponsableInterno}
                  />
                </InputGroup>
                <datalist id="datalistResponsablesInternosFallas">
                  {empleados.map((empleado) => (
                    <option
                      key={empleado.numEmpleado}
                      value={`${empleado.numEmpleado} - ${empleado.name}`}
                    />
                  ))}
                </datalist>
                <Form.Text className="text-muted">
                  Selecciona un empleado activo para el seguimiento interno.
                </Form.Text>
              </Col>
              <Col md={6}>
                <Form.Label>Responsable externo</Form.Label>
                <Form.Control
                  maxLength={150}
                  value={formulario.responsableExterno}
                  onChange={(e) =>
                    cambiarCampo("responsableExterno", e.target.value)
                  }
                  placeholder="Ej. proveedor o técnico externo"
                />
                <Form.Text className="text-muted">
                  Puede capturarse junto con el responsable interno.
                </Form.Text>
              </Col>
              <Col md={6}><Form.Label>Origen o motivo</Form.Label><Form.Control as="textarea" rows={3} maxLength={500} value={formulario.origen} onChange={(e) => cambiarCampo("origen", e.target.value)} /></Col>
              <Col md={6}><Form.Label>Descripción de la evidencia</Form.Label><Form.Control as="textarea" rows={3} maxLength={1000} value={formulario.descripcion} onChange={(e) => cambiarCampo("descripcion", e.target.value)} /></Col>
              <Col md={6}><Form.Label>Acción realizada</Form.Label><Form.Control as="textarea" rows={3} maxLength={1500} value={formulario.accionRealizada} onChange={(e) => cambiarCampo("accionRealizada", e.target.value)} /></Col>
              <Col md={6}><Form.Label>Notas</Form.Label><Form.Control as="textarea" rows={3} maxLength={1000} value={formulario.notas} onChange={(e) => cambiarCampo("notas", e.target.value)} /></Col>
              <Col md={12}>{seccionArchivos(true, modo !== "INSERTAR")}</Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="modal-acciones-movil">
            <Button variant="secondary" onClick={() => setMostrarFormulario(false)} disabled={guardando}>Cancelar</Button>
            <Button type="submit" disabled={guardando}>{guardando ? <><Spinner size="sm" className="me-2" />Guardando...</> : modo === "COMPLETAR" ? "Marcar como completada" : "Guardar"}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={mostrarNuevoCatalogo}
        onHide={() => !guardandoCatalogo && setMostrarNuevoCatalogo(false)}
        centered
      >
        <Form onSubmit={guardarNuevoCatalogo}>
          <Modal.Header closeButton>
            <Modal.Title>
              Agregar opción
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Label>
              Nuevo valor para {configuracionCatalogos[tipoNuevoCatalogo].etiqueta} *
            </Form.Label>
            <Form.Control
              autoFocus
              required
              maxLength={configuracionCatalogos[tipoNuevoCatalogo].maxLength}
              value={valorNuevoCatalogo}
              onChange={(e) => setValorNuevoCatalogo(e.target.value)}
              placeholder="Escribe el nuevo valor"
            />
            <Form.Text className="text-muted">
              Al guardar, quedará disponible para las siguientes bitácoras.
            </Form.Text>
          </Modal.Body>
          <Modal.Footer className="modal-acciones-movil">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setMostrarNuevoCatalogo(false)}
              disabled={guardandoCatalogo}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={guardandoCatalogo}>
              {guardandoCatalogo ? (
                <><Spinner size="sm" className="me-2" />Guardando...</>
              ) : (
                "Agregar opción"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal className="bitacora-fallas" show={detalle !== null} onHide={() => setDetalle(null)} size="lg" centered fullscreen="sm-down">
        <Modal.Header closeButton><Modal.Title>Detalle de la evidencia #{detalle?.idEvidencia}</Modal.Title></Modal.Header>
        <Modal.Body>{detalle && (
          <Row className="g-3">
            <Col md={4}><strong>Tipo</strong><div>{detalle.tipoEvidencia}</div></Col><Col md={5}><strong>Título de la evidencia</strong><div>{detalle.titulo}</div></Col><Col md={3}><strong>Estatus</strong><div>{badgeEstatus(detalle.estatus)} {!detalle.activo && <Badge bg="danger">ELIMINADA</Badge>}</div></Col>
            <Col md={6}><strong>Evento</strong><div>{formatoFecha(detalle.fechaEvento)}</div></Col><Col md={6}><strong>Cierre</strong><div>{formatoFecha(detalle.fechaCierre)}</div></Col>
            <Col md={4}><strong>Localidad</strong><div>{detalle.localidad || "—"}</div></Col><Col md={4}><strong>Área</strong><div>{detalle.areaRelacionada || "—"}</div></Col><Col md={4}><strong>Departamento</strong><div>{detalle.departamento || "—"}</div></Col>
            <Col md={6}><strong>Personas o clientes relacionados</strong><div>{detalle.personasAfectadas || "—"}</div></Col><Col md={6}><strong>Duración</strong><div>{detalle.duracionTexto || "—"}</div></Col>
            <Col md={6}><strong>Responsable interno</strong><div>{detalle.nombreResponsableInterno ? `${detalle.numEmpleadoResponsableInterno} - ${detalle.nombreResponsableInterno}` : detalle.numEmpleadoResponsableInterno || "—"}</div></Col>
            <Col md={6}><strong>Responsable externo</strong><div>{detalle.responsableExterno || "—"}</div></Col>
            <Col md={12}><strong>Origen o motivo</strong><div>{detalle.origen || "—"}</div></Col><Col md={12}><strong>Descripción</strong><div>{detalle.descripcion || "—"}</div></Col><Col md={12}><strong>Acción realizada</strong><div>{detalle.accionRealizada || "—"}</div></Col><Col md={12}><strong>Notas</strong><div>{detalle.notas || "—"}</div></Col>
            <Col md={12}><small className="text-muted">Registró: {detalle.nombreEmpleadoRegistro || detalle.numEmpleadoRegistro || "—"} · Alta: {formatoFecha(detalle.fechaAlta)}</small></Col>
            <Col md={12}>{seccionArchivos(false, false)}</Col>
          </Row>
        )}</Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setDetalle(null)}>Cerrar</Button></Modal.Footer>
      </Modal>

      <MsgInformativo
        show={showMsg}
        header={header}
        msg={msg}
        msgColor={msgColor}
        time={3500}
        closeToast={() => setShowMsg(false)}
      />
    </div>
  );
};

export default BitacoraEvidencias;
